import { useRef, useEffect } from 'react';
import { ECHOES_DATA } from '@/config/echoesData';

export default function EchoesSection() {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) e.target.querySelectorAll('.reveal-el').forEach(c => c.classList.add('visible'));
    }, { threshold: 0.08 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const d = ECHOES_DATA;

  return (
    <section id="echoes" data-testid="echoes-section" ref={ref} className="py-24 md:py-40 lg:py-48 px-5 md:px-10 lg:px-20" style={{ background: 'var(--deep-black)' }}>
      {/* Header */}
      <p className="reveal-el text-[11px] tracking-[0.32em] uppercase mb-5" style={{ color: '#5C5851', fontFamily: 'Inter, sans-serif' }}>
        {d.weekLabel}
      </p>
      <h2 className="reveal-el text-[36px] md:text-[42px] italic leading-tight mb-3" style={{ color: '#C9A961', fontFamily: 'Georgia, serif' }}>
        {d.title}
      </h2>
      {d.subline && (
        <p className="reveal-el text-[15px] italic mb-16 md:mb-20" style={{ color: '#888173', fontFamily: 'Georgia, serif' }}>
          {d.subline}
        </p>
      )}
      {!d.subline && <div className="mb-14 md:mb-18" />}

      {/* Rows */}
      <div className="reveal-el">
        {d.rows.map((row, i) => (
          <a
            key={row.number}
            href={row.url}
            target={row.url !== '#' ? '_blank' : undefined}
            rel={row.url !== '#' ? 'noopener noreferrer' : undefined}
            className="group block"
            style={{ textDecoration: 'none' }}
          >
            {/* Top divider */}
            {i === 0 && <div className="h-px" style={{ background: 'rgba(201,169,97,0.15)' }} />}

            <div
              className="grid gap-x-8 md:gap-x-8 py-9 transition-all duration-300 group-hover:pl-3.5"
              style={{
                gridTemplateColumns: '56px 1fr 160px',
              }}
            >
              {/* Number */}
              <span className="text-[18px] italic pt-1 hidden md:block" style={{ color: '#993C1D', fontFamily: 'Georgia, serif' }}>
                {row.number}
              </span>
              {/* Number mobile */}
              <span className="text-[16px] italic pt-0.5 md:hidden" style={{ color: '#993C1D', fontFamily: 'Georgia, serif' }}>
                {row.number}
              </span>

              {/* Body */}
              <div className="col-span-1">
                <h3 className="text-[22px] md:text-[26px] italic leading-[1.2] mb-2.5" style={{ color: '#C9A961', fontFamily: 'Georgia, serif' }}>
                  {row.title}
                </h3>
                <p className="text-[14px] md:text-[15px] italic leading-[1.55]" style={{ color: '#888173', fontFamily: 'Georgia, serif' }}>
                  {row.description}
                </p>
                {/* Tag — mobile only */}
                <span className="md:hidden inline-block mt-3 text-[10px] tracking-[0.24em] uppercase" style={{ color: '#888173', fontFamily: 'Inter, sans-serif' }}>
                  {row.tag}
                </span>
              </div>

              {/* Tag — desktop */}
              <span className="hidden md:flex items-center justify-end text-[11px] tracking-[0.24em] uppercase text-right" style={{ color: '#888173', fontFamily: 'Inter, sans-serif' }}>
                {row.tag}
              </span>
            </div>

            {/* Hover background */}
            <style>{`
              .group:hover > div:nth-child(2) { background: rgba(201,169,97,0.02); }
            `}</style>

            {/* Bottom divider */}
            <div className="h-px" style={{ background: 'rgba(201,169,97,0.15)' }} />
          </a>
        ))}
      </div>

      {/* Foot */}
      <div className="reveal-el mt-14 md:mt-20">
        <p className="text-[14px] italic" style={{ color: '#888173', fontFamily: 'Georgia, serif' }}>
          {d.footLine}
        </p>
        <p className="text-[11px] tracking-[0.24em] uppercase mt-6" style={{ color: '#5C5851', fontFamily: 'Inter, sans-serif' }}>
          {d.nextWeek}
        </p>
      </div>
    </section>
  );
}
